import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function EarlyBirdModal({ onClose, onAccessGranted }) {
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState('form'); // 'form' | 'code'
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    what_they_do: '',
    referred_by: '',
  });
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/landing-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, form_type: 'EARLY_BIRD' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Submission failed');
        return;
      }
      toast.success("Registered. We'll reach out about community-tier access.");
      onClose();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/auth/redeem-community-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: code.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Invalid code');
        return;
      }
      const data = await res.json();
      if (setUser && data.user) setUser(data.user);
      toast.success('Community-tier access unlocked.');
      onAccessGranted?.();
      onClose();
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
  };

  const tabStyle = (active) => ({
    width: '100%',
    padding: '10px 12px',
    fontSize: '13px',
    fontWeight: 600,
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    background: active ? 'var(--bg-elevated)' : 'transparent',
    color: active ? 'var(--text-primary)' : 'var(--text-muted)',
    transition: 'all 0.15s',
  });

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg-surface)',
        borderRadius: '24px',
        border: '1px solid var(--border-default)',
        padding: '24px',
        width: '100%',
        maxWidth: '460px',
        maxHeight: 'min(92vh, 760px)',
        overflowY: 'auto',
        position: 'relative',
        boxShadow: 'var(--app-panel-shadow-soft)',
      }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute', top: '16px', right: '16px',
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', padding: '4px',
          }}
        >
          <X size={18} />
        </button>

        <div style={{ paddingRight: '24px' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: '28px', lineHeight: 1.04, color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
            Unlock Community Access
          </h2>
        </div>
        <p style={{ margin: '0 0 18px', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.65, maxWidth: '32ch' }}>
          Join the Early Believer Community or enter an access code to unlock an approved community-tier program.
        </p>

        {/* Tab toggle */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px', background: 'color-mix(in srgb, var(--bg-elevated) 88%, transparent)', borderRadius: '16px', padding: '6px', marginBottom: '20px' }}>
          <button type="button" style={tabStyle(mode === 'form')} onClick={() => setMode('form')}>
            Join Community
          </button>
          <button type="button" style={tabStyle(mode === 'code')} onClick={() => setMode('code')}>
            Enter Code
          </button>
        </div>

        {mode === 'code' ? (
          !user ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
                You need to be signed in to redeem an access code.
              </p>
              <a href="/auth" className="advoq-btn-primary" style={{ display: 'inline-block', padding: '10px 24px', borderRadius: '10px', textDecoration: 'none', fontSize: '14px' }}>
                Sign In
              </a>
            </div>
          ) : (
          <form onSubmit={handleCodeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={labelStyle}>Access Code</label>
              <input
                className="advoq-input"
                required
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="Enter your access code"
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="advoq-btn-primary"
              disabled={submitting || !code.trim()}
              style={{ marginTop: '8px' }}
            >
              {submitting ? 'Verifying…' : 'Unlock Community Tier'}
            </button>
          </form>
          )
        ) : (
          <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={labelStyle}>Name</label>
              <input
                className="advoq-input"
                required
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                placeholder="Your name"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={labelStyle}>Email</label>
              <input
                className="advoq-input"
                type="email"
                required
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
                placeholder="you@firm.com"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={labelStyle}>Phone</label>
              <input
                className="advoq-input"
                required
                value={form.phone}
                onChange={e => handleChange('phone', e.target.value)}
                placeholder="+91 98765 43210"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={labelStyle}>What do you do?</label>
              <input
                className="advoq-input"
                required
                value={form.what_they_do}
                onChange={e => handleChange('what_they_do', e.target.value)}
                placeholder="e.g. Litigation lawyer at Delhi HC"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={labelStyle}>
                Who referred you? <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
              </label>
              <input
                className="advoq-input"
                value={form.referred_by}
                onChange={e => handleChange('referred_by', e.target.value)}
                placeholder="Name or referral code"
              />
            </div>
            <button
              type="submit"
              className="advoq-btn-primary"
              disabled={submitting}
              style={{ marginTop: '10px', justifyContent: 'center', minHeight: '46px', borderRadius: '14px' }}
            >
              {submitting ? 'Submitting…' : 'Register Interest'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
